// Pure logic extracted from NavBar.tsx — no component refs, no state setters,
// no store reads, no DOM side-effects. Each function depends only on its args.

/**
 * B1 — Port of Angular private extractLastSegment().
 * Returns the last path segment of a URL, URL-decoded, ignoring trailing slashes.
 * e.g. '/similar/a-b' → 'a-b', '/similar/a-b/' → 'a-b'.
 */
export function extractLastSegment(url: string): string {
  // Trim the URL to remove any trailing slashes
  const trimmedUrl = url.endsWith('/') ? url.slice(0, -1) : url;

  // Split the URL by slashes and pop the last non-empty segment
  return decodeURIComponent(trimmedUrl.split('/').pop() || '');
}

/**
 * B2 — Port of Angular TotalActiveFiltersPipe
 * (src/app/pipes/total-active-filters.pipe.ts).
 * Increments the active-filter count by 1 when the filter family title is
 * 'Lyrical Theme' AND a lyricsContains value is present (the pipe added +1
 * because lyricsContains is stored outside the regular filter count).
 */
export function totalActiveFiltersPipe(
  totalActiveFilters: number,
  title: string,
  lyricsContains?: string | null
): number {
  if (title === 'Lyrical Theme' && lyricsContains) {
    return totalActiveFilters + 1;
  }
  return totalActiveFilters;
}

/**
 * B4 — Pure file-type predicates.
 * Accept any object with a `type` string (File, DataTransferItem, or a plain
 * {type} shape). Used in onPaste to filter clipboard files without coupling
 * to the Zustand store computed fields (which are set later, inside changeFile).
 */
export function isImageFile(file: { type: string }): boolean {
  return file.type.startsWith('image/');
}

export function isVideoFile(file: { type: string }): boolean {
  return file.type.startsWith('video/');
}

export function isAudioFile(file: { type: string }): boolean {
  return file.type.startsWith('audio/');
}

/** Convenience: true when the file is an image OR a video (upload-eligible). */
export function isImageOrVideoFile(file: { type: string }): boolean {
  return isImageFile(file) || isVideoFile(file);
}

/**
 * B5 — Pure message-selector core of getNoMoreResultsMessage().
 * Given the selected sort name and whether the search was artwork-based with
 * no active filters, returns the appropriate "no more results" message string
 * (or null when the caller should fall through to the default message).
 *
 * The outer function in NavBar handles all store reads / writes; this function
 * only encodes the decision tree for the message text.
 *
 * @param artistNotFound   - true when the artist lookup returned no results.
 * @param hasSongs         - true when songs.length > 0 (skip the message).
 * @param filtersEmpty     - true when areFiltersEmpty() is true.
 * @param hasSelectedArtwork - true when selectedArtwork is set.
 * @param sortName         - the name of the currently selected sort-by option
 *                           (undefined when none is selected).
 * @returns the message string, or null to mean "use the default message".
 *          Returns the special sentinel `'__skip__'` when nothing should be set.
 */
export function noMoreResultsMessageFor(
  artistNotFound: boolean,
  hasSongs: boolean,
  filtersEmpty: boolean,
  hasSelectedArtwork: boolean,
  sortName: string | undefined
): string | '__skip__' | null {
  if (artistNotFound) {
    return "We couldn't find a perfect match. Try tweaking your search.";
  }
  if (hasSongs) {
    return '__skip__';
  }
  if (filtersEmpty && hasSelectedArtwork) {
    if (!sortName) {
      return '__skip__';
    }
    // NOTE(port): The original Angular code set 'Weighted Similarity' message inside
    // the switch case but then fell through to also build and overwrite the message
    // with the generic "try a different sorting category" string (result stayed ' ').
    // The net result for 'Weighted Similarity' is the same "try a different sorting
    // category" message — matching the original double-write behavior exactly.
    let adverb = ' ';
    switch (sortName) {
      case 'Production & Sound':
        adverb = ' sonically';
        break;
      case 'Music & Arrangement':
        adverb = ' musically';
        break;
      case 'Theme & Mood':
        adverb = ' lyrically';
        break;
      case 'Writing Style':
        adverb = ' stylistically';
        break;
    }
    return `Oops! Looks like we can't use this reference song to find${adverb} similar songs. Please try a different sorting category.`;
  }
  // Fall through to default — caller should use getDefaultNoMoreResultsMessage()
  return null;
}

/**
 * B6 — True when the current route carries any query parameter, so the home
 * page should be dismissed and the URL's filters/search applied.
 *
 * Pass `window.location.search` (the basename-independent query string, e.g.
 * '?id_2=green&id_663=green'). The leading '?' is optional.
 *
 * NOTE(port): the original handleNavigationEnd derived this from
 * `window.location.pathname + window.location.search` and tested
 * `split('/')[1].includes('?')`. That matched Angular's `urlAfterRedirects`,
 * which is router-relative (basename stripped). In the React port pathname
 * keeps the production '/songhunt/' base, so split('/')[1] is 'songhunt' — the
 * '?' lives in the next segment — and the check silently failed, leaving users
 * on the home page with their URL filters never displayed. Reading the search
 * string directly is base-independent.
 */
export function routeHasQueryParams(search: string): boolean {
  return !new URLSearchParams(search).keys().next().done;
}

/**
 * B7 — Decide what a back/forward navigation (popstate) to `eventHref` should do.
 *
 * - `appHomeHref`  = `location.origin + import.meta.env.BASE_URL`
 *   (prod `…/songhunt/`, dev `…/`) — the app's own home URL.
 * - `bareRootHref` = `location.origin + '/'` — the bare domain root (distinct
 *   from appHomeHref only in prod, where the app lives under `/songhunt/`).
 * - `hasVisualSearchSession` = an uploaded image/video + its prompt suggestions
 *   are still held in memory (navbar-store.fileUploadResponse).
 *
 * Going to the app's own home is normally an intentional HARD RESET — reload to
 * BASE_URL (same as the logo's goBackToHomePage), which wipes all in-memory
 * state. EXCEPTION: when a visual-search session is active, return home SOFTLY
 * (no reload) so the uploaded media + 3 prompts survive. This mirrors the
 * Angular app, which kept them as in-memory signals across the same soft home
 * transition (its resetAllToStart preserves fileUploadResponse when set).
 */
export type PopstateAction =
  | 'reload-base'    // hard reload to BASE_URL (app-home reset)
  | 'reload-root'    // hard reload to '/' (leaving the app entirely)
  | 'show-home-soft' // return to the home view without reloading (keep session)
  | 'sync-from-url'; // non-home URL — derive the view from the URL

export function decidePopstateAction(
  eventHref: string | undefined,
  appHomeHref: string,
  bareRootHref: string,
  hasVisualSearchSession: boolean
): PopstateAction {
  if (eventHref === appHomeHref) {
    return hasVisualSearchSession ? 'show-home-soft' : 'reload-base';
  }
  if (eventHref === bareRootHref) {
    return 'reload-root';
  }
  return 'sync-from-url';
}
